import { Button, FormError, Input, PageTitle } from '../../components';
import {
    ButtonGroup,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    InputGroup,
    InputGroupText,
    Label,
    Modal,
    Row,
} from 'reactstrap';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { Link, Redirect } from 'react-router-dom';
import { ProfessionDto } from '../../common/Types';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { systemAdministration } from '../../services/SystemAdministration';
import { toast } from 'react-toastify';
import NewProfessionForm from '../../components/NewProfessionForm';

const InactiveDiv = styled.div`
    div {
        display: inline-block;
    }

    > div {
        padding-left: 16px;
    }

    div:last-of-type {
        float: right;
    }
`;

const Professions = () => {
    // List of active professions
    const [activeProfessions, setActiveProfessions] = useState<ProfessionDto[]>();
    // Seperate list to track inactive professions
    const [inactiveProfessions, setInactiveProfessions] = useState<ProfessionDto[]>([]);
    // Handles how many professions to drop into each column
    const [profPerCol, setProfPerCol] = useState(0);
    // Search form
    const { register: registerSearch, handleSubmit: handleSearchSubmit } = useForm();
    // Profession edit form
    const {
        register: registerProfessionEdit,
        handleSubmit: handleProfessionSubmit,
        control,
        getValues,
        reset,
        setValue,
        formState: { errors },
    } = useForm();
    const { fields, append } = useFieldArray({
        control,
        name: 'professions',
    });
    // Tracking original list to reset ui after filtering
    const [originalProfessions, setOriginalProfessions] = useState<ProfessionDto[]>();
    const [editing, setEditing] = useState(false);

    const [showNewProfession, setShowNewProfession] = useState<boolean>(false);
    const toggleShowNewProfession = () => setShowNewProfession(!showNewProfession);
    const [createdProfessionId, setCreatedProfessionId] = useState<number>();

    const toggleEdit = () => {
        setEditing(!editing);
    };

    useEffect(() => {
        let isMounted = true;

        const getProfessions = () => {
            systemAdministration
                .getProfessions()
                .then((response) => {
                    if (isMounted) {
                        setOriginalProfessions(response);
                        setActiveProfessions(response.filter((x) => !x.isDeleted));
                        setInactiveProfessions(response.filter((x) => x.isDeleted));
                        setProfPerCol(Math.round(response.length / 4));
                        append(response.filter((x) => !x.isDeleted));
                    }
                })
                .catch(() => {
                    if (isMounted) {
                        toast.error('Failed to get Profession list.');
                    }
                });
        };

        getProfessions();

        return () => {
            isMounted = false;
        };
    }, [setActiveProfessions, setInactiveProfessions, setOriginalProfessions, setProfPerCol, append]);

    const onSearchSubmit = (search) => {
        if (search.text !== '') {
            setActiveProfessions(
                activeProfessions?.filter(
                    (x) => x.title.toLocaleLowerCase().indexOf(search.text.toLocaleLowerCase()) > -1,
                ),
            );
            setInactiveProfessions(
                inactiveProfessions?.filter(
                    (x) => x.title.toLocaleLowerCase().indexOf(search.text.toLocaleLowerCase()) > -1,
                ),
            );
        } else {
            setActiveProfessions(originalProfessions?.filter((x) => !x.isDeleted));
            setInactiveProfessions(originalProfessions?.filter((x) => x.isDeleted) ?? []);
        }
    };

    const onProfessionSubmit = (formData) => {
        const toastId = toast.info('Saving settings...');

        systemAdministration
            .saveProfessions(formData.professions.map((person) => new ProfessionDto(person)))
            .then((response) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Settings Saved.',
                });
                setOriginalProfessions(response);
                setActiveProfessions(response.filter((x) => !x.isDeleted));
                setInactiveProfessions(response.filter((x) => x.isDeleted));
                setProfPerCol(Math.round(response.length / 4));
                append(response.filter((x) => !x.isDeleted));
                toggleEdit();
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save settings.',
                });
            });
    };

    // Sets profession to active or inactive
    const toggleProfession = (i) => {
        const formValues = getValues();
        const current = formValues.professions[i];
        (setValue as any)(`professions[${i}]`, { ...current, isDeleted: !JSON.parse(current.isDeleted) });
        reset(getValues());
    };

    const ProfFormGroup = (props) => {
        return (
            <FormGroup>
                <input
                    type="hidden"
                    {...registerProfessionEdit(`professions[${props.index}].professionId` as any)}
                    defaultValue={props.profession.professionId}
                />
                <input
                    type="hidden"
                    {...registerProfessionEdit(`professions[${props.index}].webSiteSearchCount` as any)}
                    defaultValue={props.profession.webSiteSearchCount}
                />
                <input
                    type="hidden"
                    {...registerProfessionEdit(`professions[${props.index}].isDeleted` as any)}
                    defaultValue={props.profession.isDeleted}
                />
                <input
                    type="hidden"
                    {...registerProfessionEdit(`professions[${props.index}].armsUser` as any)}
                    defaultValue={props.profession.armsUser}
                />
                <input
                    type="hidden"
                    {...registerProfessionEdit(`professions[${props.index}].coaId` as any)}
                    defaultValue={props.profession.coaId}
                />
                {!JSON.parse(props.profession.isDeleted) ? (
                    <>
                        <InputGroup>
                            <Input
                                defaultValue={props.profession.title}
                                {...registerProfessionEdit(`professions[${props.index}].title` as any, {
                                    required: true,
                                })}
                                readOnly={JSON.parse(props.profession.isDeleted ?? false)}
                            />
                            <InputGroupText>
                                <Button color={'info'} onClick={() => toggleProfession(props.index)}>
                                    <i className={'remixicon-eye-line'} />
                                </Button>
                            </InputGroupText>
                        </InputGroup>
                        <FormError
                            name={`professions[${props.index}].title`}
                            message="A name is required."
                            errors={errors}
                        />
                    </>
                ) : (
                    <>
                        <InactiveDiv>
                            <div style={{ textDecoration: 'line-through', lineHeight: '38px' }}>
                                {props.profession.title}
                            </div>
                            <ButtonGroup>
                                <Button color={'secondary'} onClick={() => toggleProfession(props.index)}>
                                    <i className={'remixicon-eye-off-line'} />
                                </Button>
                            </ButtonGroup>
                        </InactiveDiv>
                        <input
                            type="hidden"
                            {...registerProfessionEdit(`professions[${props.index}].title` as any)}
                            defaultValue={props.profession.title}
                        />
                    </>
                )}
            </FormGroup>
        );
    };

    return (
        <>
            {createdProfessionId && <Redirect to={`/system-admin/profession/${createdProfessionId}`} />}
            {!createdProfessionId && (
                <>
                    <PageTitle className={`d-flex justify-content-between py-2`}>
                        <h4>{`Professions`}</h4>
                        <Button type={'button'} color={'primary'} onClick={toggleShowNewProfession}>
                            <i className={'mdi mdi-plus'} />
                            <span className={'ml-1'}>{`New Profession`}</span>
                        </Button>
                    </PageTitle>
                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <Row className="mb-3">
                                        <Col>
                                            <Form
                                                noValidate
                                                onSubmit={handleSearchSubmit(onSearchSubmit)}
                                                style={{ flexBasis: 'calc(50% - 20px)' }}
                                            >
                                                <FormGroup>
                                                    <Label>{`Search by Name`}</Label>
                                                    <InputGroup>
                                                        <Input
                                                            placeholder="Profession Name"
                                                            type="search"
                                                            {...registerSearch('text')}
                                                        />
                                                        <InputGroupText>
                                                            <Button type="submit" color="primary">
                                                                Search
                                                            </Button>
                                                        </InputGroupText>
                                                    </InputGroup>
                                                </FormGroup>
                                            </Form>
                                        </Col>
                                    </Row>
                                    {editing ? (
                                        <Form noValidate onSubmit={handleProfessionSubmit(onProfessionSubmit)}>
                                            <Row>
                                                <Col lg={3} md={6}>
                                                    {fields
                                                        .map((profession, i) => (
                                                            <ProfFormGroup key={i} profession={profession} index={i} />
                                                        ))
                                                        .slice(0, profPerCol)}
                                                </Col>
                                                <Col lg={3} md={6}>
                                                    {fields
                                                        ?.map((profession, i) => (
                                                            <ProfFormGroup key={i} profession={profession} index={i} />
                                                        ))
                                                        .slice(profPerCol, profPerCol * 2)}
                                                </Col>
                                                <Col lg={3} md={6}>
                                                    {fields
                                                        ?.map((profession, i) => (
                                                            <ProfFormGroup key={i} profession={profession} index={i} />
                                                        ))
                                                        .slice(profPerCol * 2, profPerCol * 3)}
                                                </Col>
                                                <Col lg={3} md={6}>
                                                    {fields
                                                        ?.map((profession, i) => (
                                                            <ProfFormGroup key={i} profession={profession} index={i} />
                                                        ))
                                                        .slice(profPerCol * 3)}
                                                </Col>
                                            </Row>
                                            <Row className="mt-3">
                                                <Col>
                                                    <Button type="submit">Save</Button>&nbsp;
                                                    <Button outline onClick={() => toggleEdit()}>
                                                        Cancel
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Form>
                                    ) : (
                                        <>
                                            {activeProfessions ? (
                                                <>
                                                    <Row>
                                                        <Col lg={3} md={6}>
                                                            {activeProfessions
                                                                .slice(0, profPerCol)
                                                                .map((profession, i) => (
                                                                    <div key={i}>
                                                                        <p>
                                                                            <Link
                                                                                to={`/system-admin/profession/${profession.professionId}`}
                                                                            >
                                                                                {profession.title}
                                                                            </Link>
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                        </Col>
                                                        <Col lg={3} md={6}>
                                                            {activeProfessions
                                                                .slice(profPerCol, profPerCol * 2)
                                                                .map((profession, i) => (
                                                                    <div key={i}>
                                                                        <p>
                                                                            <Link
                                                                                to={`/system-admin/profession/${profession.professionId}`}
                                                                            >
                                                                                {profession.title}
                                                                            </Link>
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                        </Col>
                                                        <Col lg={3} md={6}>
                                                            {activeProfessions
                                                                .slice(profPerCol * 2, profPerCol * 3)
                                                                .map((profession, i) => (
                                                                    <div key={i}>
                                                                        <p>
                                                                            <Link
                                                                                to={`/system-admin/profession/${profession.professionId}`}
                                                                            >
                                                                                {profession.title}
                                                                            </Link>
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                        </Col>
                                                        <Col lg={3} md={6}>
                                                            {activeProfessions
                                                                .slice(profPerCol * 3)
                                                                .map((profession, i) => (
                                                                    <div key={i}>
                                                                        <p>
                                                                            <Link
                                                                                to={`/system-admin/profession/${profession.professionId}`}
                                                                            >
                                                                                {profession.title}
                                                                            </Link>
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                        </Col>
                                                    </Row>
                                                    <Row className="mt-3">
                                                        <Col>
                                                            <Button onClick={() => toggleEdit()}>Edit</Button>
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : (
                                                <Row>
                                                    <Col lg={3} md={6}>
                                                        <Skeleton count={5} />
                                                    </Col>
                                                    <Col lg={3} md={6}>
                                                        <Skeleton count={5} />
                                                    </Col>
                                                    <Col lg={3} md={6}>
                                                        <Skeleton count={5} />
                                                    </Col>
                                                    <Col lg={3} md={6}>
                                                        <Skeleton count={5} />
                                                    </Col>
                                                </Row>
                                            )}
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                            {inactiveProfessions.length > 0 && (
                                <Card>
                                    <CardBody>
                                        <Row>
                                            <Col xs={12}>
                                                <h5>Deleted Professions</h5>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col lg={3} md={6}>
                                                {inactiveProfessions
                                                    ?.filter((x) => x.isDeleted)
                                                    .map((profession, i) => (
                                                        <div key={i}>
                                                            <p>
                                                                <Link
                                                                    to={`/system-admin/profession/${profession.professionId}`}
                                                                >
                                                                    {profession.title}
                                                                </Link>
                                                            </p>
                                                        </div>
                                                    ))
                                                    .slice(0, profPerCol)}
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}
                        </Col>
                    </Row>
                    <Modal isOpen={showNewProfession} toggle={toggleShowNewProfession}>
                        <NewProfessionForm
                            onCancel={toggleShowNewProfession}
                            onSave={(newProfessionId) => setCreatedProfessionId(newProfessionId)}
                        />
                    </Modal>
                </>
            )}
        </>
    );
};

export default Professions;
